/**
 * Frontend mirror of the backend invoicing types (backend/src/types/invoicing.ts).
 * Kept JSON-shaped — all dates are ISO strings.
 */

export type InvoiceSource = 'manual' | 'sheet-import' | 'email-auto' | 'io-pdf' | 'gam-auto';
export type DeliverySourceOfTruth = '1P' | '3P';
export type FieldConfidence = 'high' | 'medium' | 'low';
export type GamMatchMethod = 'order-number' | 'advertiser-date' | 'manual' | 'none';
export type OverDeliveryFlag = 'ok' | 'approaching' | 'exceeded';

/**
 * A billing month is ready to invoice once its status reads CONFIRMED. Single
 * source of truth for the frontend gate (mirrors the backend `isConfirmed`).
 */
export const isConfirmed = (status?: string): boolean => {
  const s = (status || '').trim();
  // Negated forms ("Not confirmed", "Unconfirmed", "Pending — confirm w/ agency")
  // must not read as confirmed. Separator = any non-alphanumeric run, so em/en
  // dashes and slashes don't defeat the guard. Mirrors backend isConfirmed.
  if (/\b(?:not|to\s+be|un|pending|awaiting|cannot|can't)[^a-z0-9]*confirm/i.test(s)) return false;
  return /\bconfirm(?:ed)?\b/i.test(s);
};

/** Single source of truth for "this invoice is 3rd-party (DV/IAS) verified" (mirrors backend isThreeP). */
export const isThreeP = (inv: Pick<Invoice, 'deliverySourceOfTruth'>): boolean =>
  inv.deliverySourceOfTruth === '3P';

export interface InvoiceRate {
  label: string;
  cpm?: number;
  flatRate?: number;
}

/**
 * One sheet row's planned line under an IO — the tracker plans impressions PER
 * LINE ITEM, and `contractedImpressions` stores their monthly sum.
 */
export interface PlannedLineItem {
  /** The row's description cell ("CTV :30", "OLV", …). Absent when blank. */
  label?: string;
  plannedImpressions: number;
  rate?: number;
  plannedTotal?: number;
}

export interface GamLink {
  gamOrderId?: string;
  gamOrderName?: string;
  gamLineItemIds: string[];
  gamUrl?: string;
  matchMethod: GamMatchMethod;
  matchConfidence: FieldConfidence;
  /** Persisted "why not synced" reason (fallback when no live-session diagnostic). Cleared once linked. */
  unmatchedReason?: string;
  unmatchedAt?: string;
}

export interface LineItemDelivery {
  lineItemId: string;
  lineItemName: string;
  delivered: number;
  goal: number;
  percentDelivered: number;
  progressPercent: number;
  /** Expected % of this line's goal by now, based on its own flight dates. */
  expectedPercent?: number;
  status: string;
  gamUrl?: string;
  /** Line item flight, "YYYY-MM-DD" (from GAM). */
  startDate?: string;
  endDate?: string;
  /**
   * Whether this line's flight overlaps the invoice's billing month. Absent on
   * pre-migration snapshots — treat as in-month.
   */
  inMonth?: boolean;
  /** This line's sheet-planned impressions (absent when unmatched/unavailable). */
  plannedImpressions?: number;
  /** Impressions this line delivered WITHIN the billing month (vs lifetime `delivered`). */
  deliveredMonth?: number;
  /**
   * The sheet PLAN GROUP this line belongs to (one sheet row trafficked as
   * several GAM lines). The plan paces the group's summed month delivery;
   * lines sharing a `key` share one plan.
   */
  planGroup?: { key: string; label?: string; plannedImpressions: number };
}

export interface DeliverySnapshot {
  impressionsDelivered1P?: number;
  impressionsDelivered3P?: number;
  /** First-party impressions delivered WITHIN the billing month (date-ranged GAM). */
  impressionsDeliveredMonth?: number;
  /** ISO timestamp the month-scoped delivered figure was last computed. */
  monthDeliveredAsOf?: string;
  /** Per-line month-ranged delivered (already re-joined onto lineItems[].deliveredMonth). */
  monthDelivered?: { byLineItem: Record<string, number>; asOf: string };
  /** Why the last monthly-delivered refresh failed (cleared on success/re-link). */
  monthDeliveredError?: { message: string; at: string };
  percentDelivered?: number;
  progressPercent?: number;
  expectedPercent?: number;
  isPacingAhead?: boolean;
  isPacingBehind?: boolean;
  daysRemaining?: number;
  deliveryIndicator?: string;
  overDeliveryRatio?: number;
  overDeliveryFlag?: OverDeliveryFlag;
  lineItems?: LineItemDelivery[];
  /**
   * Which line items the top-level pacing covers: 'in-month' = only lines
   * flighted in the billing month (normal); 'all-line-items' = no line
   * overlapped the month (bad/missing dates) so pacing spans the whole order.
   */
  pacingScope?: 'in-month' | 'all-line-items';
  /** The resolved billing window pacing was scoped to, "YYYY-MM-DD". */
  pacingWindowStart?: string;
  pacingWindowEnd?: string;
  lastPacingSyncAt?: string | null;
}

export interface FieldProvenance {
  source: InvoiceSource;
  confidence?: FieldConfidence;
  setBy?: string;
  setAt?: string;
}

export interface BillToOverride {
  company?: string;
  attn?: string;
  email?: string;
  addressLines?: string[];
}

export interface IoBackfillMarker {
  at: string;
  fileId: string;
  confidence: FieldConfidence;
  filledFields: string[];
}

export interface GeneratedInvoiceStamp {
  invoiceNumber: string;
  webViewLink: string;
  generatedAt: string;
}

export interface InvoiceAuditEntry {
  at: string;
  by: string;
  field: string;
  from: unknown;
  to: unknown;
}

export interface Invoice {
  id: string;
  campaignKey: string;
  /** Internal "Campaign ID" (CP…) — for grouping/display, NOT GAM matching. */
  campaignOrderId?: string;
  /** GAM "Order #" (O-… or numeric) — the value used to auto-link to GAM. */
  gamOrderNumber?: string;
  monthKey: string;
  year: number;
  advertiser: string;
  agency?: string;
  ioLink?: string;
  description?: string;
  monthlyFlightStart?: string;
  monthlyFlightEnd?: string;
  totalCampaignFlightStart?: string;
  totalCampaignFlightEnd?: string;
  rates: InvoiceRate[];
  contractedImpressions?: number;
  plannedTotal?: number;
  /** Per-row planned breakdown from the sheet (contractedImpressions = their sum). */
  plannedLineItems?: PlannedLineItem[];
  deliverySourceOfTruth: DeliverySourceOfTruth;
  delivery: DeliverySnapshot;
  billableTotal?: number;
  status?: string;
  revisedStatus?: string;
  notes?: string;
  billToOverride?: BillToOverride;
  gam: GamLink;
  ioBackfill?: IoBackfillMarker;
  generatedInvoice?: GeneratedInvoiceStamp;
  source: InvoiceSource;
  extractionConfidence?: FieldConfidence;
  needsReview: boolean;
  reviewedBy?: string;
  reviewedAt?: string | null;
  fieldProvenance?: Record<string, FieldProvenance>;
  sourceMessageId?: string;
  sourceAttachmentName?: string;
  auditLog?: InvoiceAuditEntry[];
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  /**
   * Decoration added by GET /invoices (not stored): a month-true delivered
   * report is currently running for this invoice. Drives "Syncing GAM…" vs
   * "Awaiting GAM sync" and the dashboard's polling loop.
   */
  monthRefreshInFlight?: boolean;
}

/** Editable fields for a PATCH. Mirror of backend PatchInvoiceRequest. */
export interface InvoicePatch {
  advertiser?: string;
  agency?: string;
  campaignOrderId?: string;
  /** GAM "Order #" (O-… or numeric) — paste to enable GAM auto-link. */
  gamOrderNumber?: string;
  ioLink?: string;
  description?: string;
  monthlyFlightStart?: string;
  monthlyFlightEnd?: string;
  totalCampaignFlightStart?: string;
  totalCampaignFlightEnd?: string;
  rates?: InvoiceRate[];
  contractedImpressions?: number;
  plannedTotal?: number;
  deliverySourceOfTruth?: DeliverySourceOfTruth;
  impressionsDelivered3P?: number;
  gamLineItemIds?: string[];
  billableTotal?: number;
  status?: string;
  revisedStatus?: string;
  notes?: string;
  billToOverride?: BillToOverride;
}

export interface CampaignSummary {
  campaignKey: string;
  totalMonths: number;
  totalBillable: number;
  allConfirmed: boolean;
  unconfirmedMonths: string[];
  hasUnpriced: boolean;
}

export interface InvoicingResponse {
  monthKey: string;
  invoices: Invoice[];
  campaignSummaries?: Record<string, CampaignSummary>;
  availableMonths: string[];
  lastFetchedAt: string;
}

export interface InvoicingStatus {
  canView: boolean;
  canManage: boolean;
  canBill: boolean;
}

export interface SheetColumnMapping {
  field: string;
  columnIndex: number;
  columnLetter: string;
  header: string;
}

export interface SheetInspection {
  workbook: string;
  tab: string;
  monthKey?: string;
  headerRowIndex: number;
  columns: SheetColumnMapping[];
  unmapped: string[];
  allHeaders: string[];
  sampleRows: string[][];
}

export interface ImportSummary {
  tabsProcessed: string[];
  rowsRead: number;
  created: number;
  updated: number;
  skipped: number;
  skipReasons?: {
    noAdvertiser: number;
    noMonth: number;
    upsertFailed: number;
  };
  parseErrors: string[];
  lowConfidenceCols: string[];
  /** Informational notes (e.g. fragmented sheet rows merged) — not errors. */
  notes?: string[];
  dryRun?: boolean;
  inspections?: SheetInspection[];
  purged?: number;
}

export interface PacingSyncSummary {
  invoicesConsidered: number;
  invoicesUpdated: number;
  lineItemsQueried: number;
  overDeliveryExceeded: number;
  overDeliveryApproaching: number;
  /** Threshold emails sent by the post-sync alert pass. */
  alertsSent?: number;
  errors: string[];
}

// ---- Phase 2: auto-link, IO backfill, billing contacts, generated invoices ----

export interface GamLinkSuggestion {
  invoiceId: string;
  advertiser: string;
  monthKey: string;
  gamLineItemIds: string[];
  gamOrderId?: string;
  gamOrderName?: string;
  matchMethod: GamMatchMethod;
  matchConfidence: FieldConfidence;
  matchedOrders: Array<{ id: string; name: string }>;
  reason: string;
  applied: boolean;
}

export interface UnmatchedInvoiceDetail {
  invoiceId: string;
  advertiser: string;
  monthKey: string;
  gamOrderNumber?: string;
  /** Why this invoice didn't link to a GAM order (shown on the row). */
  reason: string;
}

export interface GamAutoLinkSummary {
  invoicesConsidered: number;
  applied: number;
  suggested: number;
  unmatched: number;
  suggestions: GamLinkSuggestion[];
  /** Per-invoice reason for every unmatched invoice. */
  unmatchedDetails: UnmatchedInvoiceDetail[];
  errors: string[];
  pacing?: PacingSyncSummary;
}

export interface IoBackfillSummary {
  invoicesConsidered: number;
  invoicesEnriched: number;
  skippedNoLink: number;
  skippedAlready: number;
  fieldsFilled: number;
  errors: string[];
}

export interface BillingContactsImportSummary {
  workbook?: string;
  tab?: string;
  imported: number;
  errors: string[];
}

export interface DuplicateMember {
  id: string;
  campaignKey: string;
  advertiser: string;
  monthKey: string;
  source: string;
  billableTotal?: number;
  hasManualEdits: boolean;
  isGenerated: boolean;
  gamLineItemCount: number;
  isKeeper: boolean;
  updatedAt: string;
}

export interface DuplicateGroup {
  monthKey: string;
  bridge: string;
  keeperId: string;
  members: DuplicateMember[];
  conflict?: string;
}

export interface DedupeResult {
  dryRun: boolean;
  monthKey: string;
  invoicesScanned: number;
  groupsFound: number;
  groupsMerged: number;
  groupsSkipped: number;
  docsDeleted: number;
  groups: DuplicateGroup[];
  errors: string[];
}

export interface BillToSnapshot {
  company?: string;
  attn?: string;
  email?: string;
  addressLines?: string[];
  source: 'override' | 'contacts' | 'fallback';
}

export interface GeneratedInvoiceLineItem {
  monthKey: string;
  monthLabel: string;
  flightStart?: string;
  flightEnd?: string;
  contractedImpressions?: number;
  deliveredImpressions?: number;
  deliveredSource?: '1P' | '3P';
  deliveredAsOf?: string;
  billableTotal: number;
  description?: string;
}

export interface GeneratedInvoice {
  invoiceNumber: string;
  campaignKey: string;
  monthKey?: string;
  invoiceId?: string;
  campaignOrderId?: string;
  advertiser: string;
  agency?: string;
  monthKeys: string[];
  campaignFlightStart?: string;
  campaignFlightEnd?: string;
  lineItems: GeneratedInvoiceLineItem[];
  totalBillable: number;
  billTo: BillToSnapshot;
  issueDate: string;
  dueDate: string;
  terms: string;
  driveFileId: string;
  webViewLink: string;
  downloadLink: string;
  generatedAt: string;
  generatedBy: string;
}
